import httpService from "./httpService";
import {
  CreateStrategyRequest,
  FactorRow,
  ManagedStrategyDailyEarningsRow,
  ManagedStrategyDetail,
  ManagedStrategyEarningsDayResponse,
  ManagedStrategyEarningsListParams,
  ManagedStrategyEarningsRawRow,
  MessageOnly,
  MessageWithStrategy,
  PagedFactorList,
  PagedManagedStrategyDailyEarningsList,
  PagedManagedStrategyList,
  ReplaceCompositionRequest,
  SnapshotsResponse,
  SnapshotRow,
  UpdateStrategyRequest,
} from "../types/managedStrategy";

// Admin service for Managed Strategies, wrapping the ADMIN-only
// `/v3/admin/managed-strategies` endpoints. Composition writes (create's
// initial set, and replaceComposition) are the only path that sets a
// strategy's holdings -- updateMetadata never touches composition.

const BASE = "/v3/admin/managed-strategies";
const EARNINGS_MAX_PAGE_SIZE = 200;
const EARNINGS_SAFETY_PAGE_CAP = 50;

export interface GetFactorsParams {
  page?: number;
  pageSize?: number;
  from?: string;
  to?: string;
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) parts.push(`${key}=${encodeURIComponent(value)}`);
  }
  return parts.length > 0 ? `?${parts.join("&")}` : "";
}

class ManagedStrategyService {
  async list(page?: number, pageSize?: number): Promise<PagedManagedStrategyList> {
    const query = buildQuery({ page, pageSize });
    const response = await httpService.get<PagedManagedStrategyList>(`${BASE}${query}`);
    return response.data;
  }

  async get(strategyId: number): Promise<ManagedStrategyDetail> {
    const response = await httpService.get<ManagedStrategyDetail>(`${BASE}/${strategyId}`);
    return response.data;
  }

  async create(body: CreateStrategyRequest): Promise<ManagedStrategyDetail> {
    const response = await httpService.post<MessageWithStrategy>(BASE, body);
    return response.data.data;
  }

  async updateMetadata(
    strategyId: number,
    body: UpdateStrategyRequest
  ): Promise<ManagedStrategyDetail> {
    const response = await httpService.put<MessageWithStrategy>(`${BASE}/${strategyId}`, body);
    return response.data.data;
  }

  async remove(strategyId: number): Promise<MessageOnly> {
    const response = await httpService.delete<MessageOnly>(`${BASE}/${strategyId}`);
    return response.data;
  }

  async replaceComposition(
    strategyId: number,
    body: ReplaceCompositionRequest
  ): Promise<ManagedStrategyDetail> {
    const response = await httpService.put<MessageWithStrategy>(
      `${BASE}/${strategyId}/composition`,
      body
    );
    return response.data.data;
  }

  async getSnapshots(strategyId: number, from?: string, to?: string): Promise<SnapshotRow[]> {
    const query = buildQuery({ from, to });
    const response = await httpService.get<SnapshotsResponse>(
      `${BASE}/${strategyId}/snapshots${query}`
    );
    return response.data.snapshots;
  }

  async getFactors(strategyId: number, params?: GetFactorsParams): Promise<FactorRow[]> {
    const page = await this.getFactorsPage(strategyId, params);
    return page.items;
  }

  async getFactorsPage(
    strategyId: number,
    params?: GetFactorsParams
  ): Promise<PagedFactorList> {
    const query = buildQuery({
      page: params?.page,
      pageSize: params?.pageSize,
      from: params?.from,
      to: params?.to,
    });
    const response = await httpService.get<PagedFactorList>(
      `${BASE}/${strategyId}/factors${query}`
    );
    return response.data;
  }

  async listDailyEarnings(
    params: ManagedStrategyEarningsListParams = {}
  ): Promise<PagedManagedStrategyDailyEarningsList> {
    const query = buildQuery({
      page: params.page,
      pageSize: params.pageSize,
      fromDate: params.fromDate,
      toDate: params.toDate,
    });
    const response = await httpService.get<PagedManagedStrategyDailyEarningsList>(
      `${BASE}/earnings${query}`
    );
    return response.data;
  }

  async fetchAllDailyEarnings(params: {
    fromDate?: string;
    toDate?: string;
  }): Promise<{
    items: ManagedStrategyDailyEarningsRow[];
    totalItems: number;
    truncated: boolean;
  }> {
    const first = await this.listDailyEarnings({
      ...params,
      page: 1,
      pageSize: EARNINGS_MAX_PAGE_SIZE,
    });
    const items = [...first.items];
    const totalPages = Math.min(first.totalPages, EARNINGS_SAFETY_PAGE_CAP);

    for (let page = 2; page <= totalPages; page++) {
      const next = await this.listDailyEarnings({
        ...params,
        page,
        pageSize: EARNINGS_MAX_PAGE_SIZE,
      });
      items.push(...next.items);
    }

    return {
      items,
      totalItems: first.totalItems,
      truncated: first.totalPages > EARNINGS_SAFETY_PAGE_CAP,
    };
  }

  async getEarningsRowsForDate(date: string): Promise<ManagedStrategyEarningsRawRow[]> {
    const response = await httpService.get<ManagedStrategyEarningsDayResponse>(
      `${BASE}/earnings/${date}`
    );
    return response.data.items;
  }
}

export const managedStrategyService = new ManagedStrategyService();
export default managedStrategyService;
